import { useCallback, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { AuthContext, LoggedUser } from '../context/AuthProvider';
import { useAllieStorage } from './useAllieStorage';
import { CHANGE_EMAIL_PASSWORD, CHANGE_PIN, EMAIL_LOGIN, PIN_LOGIN } from '../data';
import { UserDto } from '../helpers';
import { UtilsContext } from '../context';
import { SessionEndType, TrackingEvents } from '../helpers/tracking-events';

export const GUEST_UID = 0;

export const useAllieAuth = () => {
  const {
    permissions,
    loggedUser,
    tryGuestLogin,
    onLoginSuccessful,
    openLogin,
    openLoginFn,
    guestModeLoading,
    guestModeFailed,
    isChangingUser,
    setUser,
    patchLoggedUser,
  } = useContext(AuthContext);
  const { setAutoGuestEnabled } = useAllieStorage();
  const [pinLoginMutation] = useMutation(PIN_LOGIN);
  const [emailLoginMutation] = useMutation(EMAIL_LOGIN);
  const [changeEmailPasswordMutation] = useMutation(CHANGE_EMAIL_PASSWORD);
  const [changePinMutation] = useMutation(CHANGE_PIN);
  const { tracker, company } = useContext(UtilsContext);

  const isGuest = useCallback(
    (user?: LoggedUser): boolean => Boolean(user && user.id === GUEST_UID),
    []
  );

  const isAuthenticated = loggedUser && !isGuest(loggedUser);

  const login = useCallback(
    (shouldRestoreLastPage = true, onDismiss?: any) => {
      setAutoGuestEnabled(false);

      if (openLogin)
        openLogin({
          onDismiss: () => {
            setAutoGuestEnabled(true);
            onDismiss?.();
          },
          shouldRestoreLastPage,
        });
    },
    [openLogin, setAutoGuestEnabled]
  );

  const logout = useCallback(
    (shouldRestoreLastPage = true) => {
      tracker?.track(TrackingEvents.LOGIN.login_user_session_ended, {
        user_name: loggedUser?.name,
        user_client: company?.name,
        user_role: loggedUser?.role,
        session_end_type: SessionEndType.USER_LOGOUT,
      });
      if (tryGuestLogin) tryGuestLogin(true, shouldRestoreLastPage);
    },
    [tryGuestLogin]
  );

  const isInternalAccess = useCallback(async (): Promise<boolean> => new Promise((res) => {
    if (!tryGuestLogin) res(false);

    return tryGuestLogin?.(false, false, true)
      .then(() => res(true))
      .catch(() => res(false));
  }), [tryGuestLogin]);

  const pinLogin = useCallback(
    (pin: string, onSuccess?: () => void, onError?: () => void, shouldRestoreLastPage = false) =>
      pinLoginMutation({ variables: { pinLoginInput: { pin } } })
        .then(({ data }) => {
          if (onLoginSuccessful) {
            onLoginSuccessful(data.pinLogin, true, shouldRestoreLastPage, onSuccess);
          }
        })
        .catch(() => {
          if (onError) onError();
        }),
    [pinLoginMutation, onLoginSuccessful]
  );

  const emailLogin = useCallback(
    (
      email: string,
      password: string,
      onSuccess?: () => void,
      onError?: () => void,
      shouldRestoreLastPage = false
    ) =>
      emailLoginMutation({ variables: { emailLoginInput: { email, password } } })
        .then(({ data }) => {
          if (onLoginSuccessful) {
            onLoginSuccessful(data.emailLogin, true, shouldRestoreLastPage, onSuccess);
          }
        })
        .catch(() => {
          if (onError) onError();
        }),
    [emailLoginMutation, onLoginSuccessful]
  );

  const changeEmailPassword = useCallback(
    (
      email: string,
      newPassword: string,
      userId: number,
    ) => changeEmailPasswordMutation({ variables: { email, newPassword, userId } }),
    []
  );

  const changePin = useCallback(
    (
      newPin: string,
      userId: number,
      onSuccess?: () => void,
      onError?: () => void,
      shouldRestoreLastPage = false
    ) =>
      changePinMutation({ variables: { newPin, userId } })
        .then(({ data }) => {
          if (onLoginSuccessful) {
            onLoginSuccessful(data, true, shouldRestoreLastPage, onSuccess);
          }
        })
        .catch(() => {
          if (onError) onError();
        }),
    [changePinMutation, onLoginSuccessful]
  );

  const simulateLogin = useCallback((user: UserDto, cb?: () => void) => {
    setUser?.(user, cb);
  }, [setUser]);

  const updateLoggedUser = useCallback((user: Partial<UserDto>, cb?: () => void) => {
    patchLoggedUser?.(user, cb);
  }, [patchLoggedUser]);

  return {
    isAuthenticated,
    isGuest,
    loggedUser,
    login,
    logout,
    permissions,
    pinLogin,
    emailLogin,
    openLogin,
    openLoginFn,
    guestModeLoading,
    guestModeFailed,
    isChangingUser,
    changeEmailPassword,
    changePin,
    simulateLogin,
    updateLoggedUser,
    isInternalAccess,
  };
};
